import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Link as LinkIcon,
  ListAlt as ContentIcon,
  CallMerge as MergeIcon,
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiService, {
  type MediaChannelProgramKind,
  type MediaChannelProgramRow,
  type MediaChannelProgramSuggestion,
} from "../services/api";
import { slugify } from "../utils/textUtils";
import { formatDuration } from "../utils/time";

const KIND_LABELS: Record<MediaChannelProgramKind, string> = {
  content: "On demand",
  livestream: "Livestream",
};

const LEGACY_PROGRAM_LABELS: Record<MediaChannelProgramKind, string> = {
  content: "Podcast program",
  livestream: "Livestream program",
};

const ALL_KINDS: MediaChannelProgramKind[] = ["content", "livestream"];

const linkedLegacyId = (
  prog: MediaChannelProgramRow,
  kind: MediaChannelProgramKind,
): string | null =>
  kind === "content" ? prog.legacyPodcastProgramId : prog.legacyLivestreamProgramId;

const linkedLegacyName = (
  prog: MediaChannelProgramRow,
  kind: MediaChannelProgramKind,
): string | null => (kind === "content" ? prog.podcastProgramName : prog.livestreamProgramName);

const linkPayloadKey = (kind: MediaChannelProgramKind) =>
  kind === "content" ? "podcastProgramId" : "livestreamProgramId";

type KindFilter = "all" | MediaChannelProgramKind;

/** A podcast or livestream program that a channel program can link to. */
type LegacyProgram = {
  kind: MediaChannelProgramKind;
  _id: string;
  name: string;
  /** Podcast programs only; livestream programs have no slug. */
  slug: string | null;
  isActive: boolean;
  hidden: boolean;
  /** Episodes (podcast programs) or streams (livestream programs). */
  itemCount: number;
  foreignStreamChannels: Array<{ channelName: string; count: number }>;
  channelProgram: { _id: string; name: string } | null;
};

const legacyKey = (prog: LegacyProgram): string => `${prog.kind}-${prog._id}`;

const splitCsv = (value: string): string[] =>
  value
    .split(/[,;\n]+/)
    .map((part) => part.trim())
    .filter(Boolean);

const joinCsv = (items: string[] | undefined): string => (items ?? []).join(", ");

const sortByName = <T extends { name: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => a.name.localeCompare(b.name, "es", { sensitivity: "base" }));

const previewList = (items: string[] | undefined, max = 3): string => {
  const list = items ?? [];
  if (list.length === 0) return "—";
  if (list.length <= max) return list.join(", ");
  return `${list.slice(0, max).join(", ")} +${list.length - max}`;
};

function apiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { error?: string; message?: string } } }).response
    ?.data;
  return data?.error || data?.message || fallback;
}

type ProgramFormState = {
  name: string;
  slug: string;
  /** Linked podcast / livestream program id per kind ("" = not linked). */
  linkIds: Record<MediaChannelProgramKind, string>;
  aliases: string;
  keywords: string;
  cues: string;
  hashtags: string;
  youtubePlaylistIds: string;
  isActive: boolean;
  hidden: boolean;
  isDigital: boolean;
};

const emptyForm = (): ProgramFormState => ({
  name: "",
  slug: "",
  linkIds: { content: "", livestream: "" },
  aliases: "",
  keywords: "",
  cues: "",
  hashtags: "",
  youtubePlaylistIds: "",
  isActive: true,
  hidden: false,
  isDigital: false,
});

const formFromProgram = (prog: MediaChannelProgramRow): ProgramFormState => ({
  name: prog.name ?? "",
  slug: prog.slug ?? "",
  linkIds: {
    content: prog.legacyPodcastProgramId ?? "",
    livestream: prog.legacyLivestreamProgramId ?? "",
  },
  aliases: joinCsv(prog.aliases),
  keywords: joinCsv(prog.keywords),
  cues: joinCsv(prog.cues),
  hashtags: joinCsv(prog.hashtags),
  youtubePlaylistIds: joinCsv(prog.youtubePlaylistIds),
  isActive: prog.isActive !== false,
  hidden: prog.hidden === true,
  isDigital: prog.isDigital === true,
});

const suggestionKey = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const SUGGESTION_SOURCE_LABELS: Record<Exclude<MediaChannelProgramSuggestion["source"], "program">, string> = {
  title: "titles",
  description: "descriptions",
  tags: "YouTube tags",
};

const suggestionTooltip = (suggestion: MediaChannelProgramSuggestion): string =>
  suggestion.source === "program"
    ? "From the linked podcast / livestream program"
    : `In ${Math.round(suggestion.share * 100)}% of the program's recent ${
        SUGGESTION_SOURCE_LABELS[suggestion.source]
      }`;

type ProgramFieldSuggestionsProps = {
  channelId: string;
  linkIds: Record<MediaChannelProgramKind, string>;
  field: "aliases" | "hashtags";
  /** Values already in the field (or otherwise covered, like the program name). */
  currentValues: string[];
  onAdd: (values: string[]) => void;
};

const ProgramFieldSuggestions: React.FC<ProgramFieldSuggestionsProps> = ({
  channelId,
  linkIds,
  field,
  currentValues,
  onAdd,
}) => {
  const enabled = Boolean(linkIds.content || linkIds.livestream);
  const suggestionsQuery = useQuery({
    queryKey: [
      "admin-media-channel-program-suggestions",
      channelId,
      linkIds.content,
      linkIds.livestream,
    ],
    queryFn: () =>
      apiService.getAdminMediaChannelProgramSuggestions(channelId, {
        ...(linkIds.content ? { podcastProgramId: linkIds.content } : {}),
        ...(linkIds.livestream ? { livestreamProgramId: linkIds.livestream } : {}),
      }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  if (!enabled) return null;
  if (suggestionsQuery.isLoading) {
    return (
      <Typography variant="caption" color="text.secondary">
        Looking for suggestions…
      </Typography>
    );
  }
  const taken = new Set(currentValues.map(suggestionKey));
  const suggestions = (suggestionsQuery.data?.[field] ?? []).filter(
    (s) => !taken.has(suggestionKey(s.value)),
  );
  if (suggestions.length === 0) return null;

  return (
    <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center" sx={{ mt: -1 }}>
      <Typography variant="caption" color="text.secondary">
        Suggested:
      </Typography>
      {suggestions.map((s) => (
        <Tooltip key={s.value} title={suggestionTooltip(s)}>
          <Chip
            size="small"
            variant="outlined"
            icon={<AddIcon />}
            color={s.source === "program" ? "primary" : "default"}
            label={field === "hashtags" ? `#${s.value}` : s.value}
            onClick={() => onAdd([s.value])}
          />
        </Tooltip>
      ))}
      {suggestions.length > 1 && (
        <Button size="small" onClick={() => onAdd(suggestions.map((s) => s.value))}>
          Add all
        </Button>
      )}
    </Stack>
  );
};

type ProgramContentTarget = {
  name: string;
  query:
    | { channelProgramId: string }
    | { podcastProgramId: string }
    | { livestreamProgramId: string };
};

const formatContentDate = (value: string | null): string =>
  value ? new Date(value).toLocaleDateString("es-CL") : "—";

const youtubeVideoId = (url: string | null): string => {
  if (!url) return "";
  try {
    const id = new URL(url).searchParams.get("v");
    if (id) return id;
  } catch {
    // Short or non-URL values fall through to the pattern below.
  }
  return url.match(/(?:v=|youtu\.be\/)([\w-]{6,})/)?.[1] ?? "";
};

type ProgramContentItem = {
  key: string;
  type: "content" | "stream";
  _id: string;
  title: string;
  url: string | null;
  videoId: string;
  date: string | null;
  durationSeconds: number | null;
  status: string | null;
  channelProgramId: string | null;
  channelProgramManual: boolean;
};

const NO_CHANNEL_PROGRAM = "__none__";
const LEGACY_MOVE_PREFIX = "legacy:";

const ProgramContentDialog: React.FC<{
  channelId: string;
  target: ProgramContentTarget | null;
  programs: MediaChannelProgramRow[];
  legacyPrograms: Record<MediaChannelProgramKind, Array<{ _id: string; name: string }>>;
  onClose: () => void;
  onAssigned: () => void;
}> = ({ channelId, target, programs, legacyPrograms, onClose, onAssigned }) => {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [moveTo, setMoveTo] = useState("");

  const legacyKind: MediaChannelProgramKind | null = !target
    ? null
    : "podcastProgramId" in target.query
      ? "content"
      : "livestreamProgramId" in target.query
        ? "livestream"
        : null;
  const currentLegacyId = !target
    ? null
    : "podcastProgramId" in target.query
      ? target.query.podcastProgramId
      : "livestreamProgramId" in target.query
        ? target.query.livestreamProgramId
        : null;
  const legacyMoveOptions = legacyKind
    ? legacyPrograms[legacyKind].filter((prog) => prog._id !== currentLegacyId)
    : [];
  const legacyMoveId = moveTo.startsWith(LEGACY_MOVE_PREFIX)
    ? moveTo.slice(LEGACY_MOVE_PREFIX.length)
    : null;
  const legacyItemType = legacyKind === "livestream" ? "stream" : "content";
  const [feedback, setFeedback] = useState<{ severity: "success" | "error"; message: string } | null>(
    null,
  );

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setSelected(new Set());
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const contentQuery = useQuery({
    queryKey: ["admin-media-channel-program-content", channelId, target?.query, search],
    queryFn: () =>
      apiService.getAdminMediaChannelProgramContent(channelId, target!.query, search || undefined),
    enabled: Boolean(target),
    placeholderData: keepPreviousData,
  });
  const data = contentQuery.data;

  const programNames = useMemo(
    () => new Map(programs.map((prog) => [prog._id, prog.name] as const)),
    [programs],
  );

  const sections = useMemo(() => {
    if (!data) return [];
    const episodes: ProgramContentItem[] = data.content.map((row) => ({
      key: `content:${row._id}`,
      type: "content",
      _id: row._id,
      title: row.title,
      url: row.url,
      videoId: youtubeVideoId(row.url),
      date: row.publishedAt,
      durationSeconds: row.durationSeconds,
      status: null,
      channelProgramId: row.channelProgramId,
      channelProgramManual: row.channelProgramManual,
    }));
    const streams: ProgramContentItem[] = data.streams.map((row) => ({
      key: `stream:${row._id}`,
      type: "stream",
      _id: row._id,
      title: row.title,
      url: row.url,
      videoId: youtubeVideoId(row.url),
      date: row.startedAt,
      durationSeconds: row.durationSeconds,
      status: row.status,
      channelProgramId: row.channelProgramId,
      channelProgramManual: row.channelProgramManual,
    }));
    const streamsByVideo = new Map<string, ProgramContentItem[]>();
    for (const stream of streams) {
      if (!stream.videoId) continue;
      const list = streamsByVideo.get(stream.videoId) ?? [];
      list.push(stream);
      streamsByVideo.set(stream.videoId, list);
    }
    const usedStreamKeys = new Set<string>();
    const paired: ProgramContentItem[] = [];
    const loneEpisodes: ProgramContentItem[] = [];
    for (const episode of episodes) {
      const matches = episode.videoId ? streamsByVideo.get(episode.videoId) : undefined;
      if (!matches?.length) {
        loneEpisodes.push(episode);
        continue;
      }
      paired.push(episode);
      for (const stream of matches) {
        if (usedStreamKeys.has(stream.key)) continue;
        paired.push(stream);
        usedStreamKeys.add(stream.key);
      }
    }
    const loneStreams = streams.filter((stream) => !usedStreamKeys.has(stream.key));
    const pairedVideos = new Set(paired.map((item) => item.videoId).filter(Boolean)).size;
    return [
      { label: "Episodes & Streams", total: pairedVideos, items: paired, showKind: true },
      { label: "Episodes", total: loneEpisodes.length, items: loneEpisodes, showKind: false },
      { label: "Streams", total: loneStreams.length, items: loneStreams, showKind: false },
    ].filter((section) => section.items.length > 0);
  }, [data]);

  const allItems = useMemo(() => sections.flatMap((section) => section.items), [sections]);

  const selectedItems = allItems.filter((item) => selected.has(item.key));
  const legacyMovableCount = selectedItems.filter((item) => item.type === legacyItemType).length;

  const assignMutation = useMutation({
    mutationFn: async (): Promise<{ moved: number; destination: string }> => {
      if (legacyKind && legacyMoveId) {
        const result = await apiService.moveAdminMediaChannelProgramContent(channelId, {
          kind: legacyKind,
          ids: selectedItems.filter((item) => item.type === legacyItemType).map((item) => item._id),
          targetProgramId: legacyMoveId,
        });
        return { moved: result.movedCount, destination: `"${result.target.name}"` };
      }
      const result = await apiService.assignAdminMediaChannelProgramContent(channelId, {
        contentIds: selectedItems.filter((item) => item.type === "content").map((item) => item._id),
        streamIds: selectedItems.filter((item) => item.type === "stream").map((item) => item._id),
        channelProgramId: moveTo === NO_CHANNEL_PROGRAM ? null : moveTo,
      });
      return {
        moved: result.contentModified + result.streamsModified,
        destination:
          moveTo === NO_CHANNEL_PROGRAM ? "no channel program" : `"${programNames.get(moveTo)}"`,
      };
    },
    onSuccess: ({ moved, destination }) => {
      setFeedback({ severity: "success", message: `${moved} item(s) moved to ${destination}` });
      setSelected(new Set());
      void queryClient.invalidateQueries({
        queryKey: ["admin-media-channel-program-content", channelId],
      });
      onAssigned();
    },
    onError: (error) =>
      setFeedback({ severity: "error", message: apiErrorMessage(error, "Could not move content") }),
  });

  const copyIds = (items: ProgramContentItem[]) => {
    const text =
      items.length === 1
        ? items[0]!._id
        : items.map((item) => `${item.type} ${item._id} ${item.title}`).join("\n");
    navigator.clipboard.writeText(text).then(
      () =>
        setFeedback({
          severity: "success",
          message: items.length === 1 ? `Copied ${items[0]!._id}` : `Copied ${items.length} IDs`,
        }),
      () => setFeedback({ severity: "error", message: "Could not copy to the clipboard" }),
    );
  };

  const toggle = (keys: string[], checked: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      for (const key of keys) {
        if (checked) next.add(key);
        else next.delete(key);
      }
      return next;
    });

  const renderChannelProgram = (item: ProgramContentItem) => {
    const name = item.channelProgramId
      ? (programNames.get(item.channelProgramId) ?? "(deleted)")
      : null;
    return (
      <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
        {name ? (
          <Typography variant="body2">{name}</Typography>
        ) : (
          <Typography variant="body2" color="text.disabled">
            None
          </Typography>
        )}
        {item.channelProgramManual && (
          <Tooltip title="Moved by hand; linking or unlinking podcast / livestream programs won't change it">
            <Chip size="small" label="manual" variant="outlined" color="warning" />
          </Tooltip>
        )}
      </Stack>
    );
  };

  const renderRows = (items: ProgramContentItem[], showKind: boolean) => {
    const keys = items.map((item) => item.key);
    const selectedCount = keys.filter((key) => selected.has(key)).length;
    const pairShade = new Map<string, boolean>();
    let shade = false;
    for (const item of items) {
      if (!item.videoId || pairShade.has(item.videoId)) continue;
      pairShade.set(item.videoId, shade);
      shade = !shade;
    }
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                size="small"
                checked={selectedCount > 0 && selectedCount === keys.length}
                indeterminate={selectedCount > 0 && selectedCount < keys.length}
                onChange={(e) => toggle(keys, e.target.checked)}
              />
            </TableCell>
            {showKind && <TableCell width={90}>Kind</TableCell>}
            <TableCell width={100}>Date</TableCell>
            <TableCell>Title</TableCell>
            <TableCell width={180}>Channel program</TableCell>
            <TableCell align="right" width={80}>
              Duration
            </TableCell>
            <TableCell width={80} />
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow
              key={item.key}
              hover
              selected={selected.has(item.key)}
              sx={
                showKind && item.videoId && pairShade.get(item.videoId)
                  ? { bgcolor: "action.hover" }
                  : undefined
              }
            >
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={selected.has(item.key)}
                  onChange={(e) => toggle([item.key], e.target.checked)}
                />
              </TableCell>
              {showKind && (
                <TableCell>{item.type === "stream" ? "Stream" : "Episode"}</TableCell>
              )}
              <TableCell>{formatContentDate(item.date)}</TableCell>
              <TableCell>
                {item.url ? (
                  <Link href={item.url} target="_blank" rel="noopener noreferrer" underline="hover">
                    {item.title || "(untitled)"}
                  </Link>
                ) : (
                  item.title || "(untitled)"
                )}
                {item.status ? (
                  <Chip size="small" label={item.status} variant="outlined" sx={{ ml: 1 }} />
                ) : null}
              </TableCell>
              <TableCell>{renderChannelProgram(item)}</TableCell>
              <TableCell align="right">
                {item.durationSeconds ? formatDuration(item.durationSeconds) : "—"}
              </TableCell>
              <TableCell padding="none" sx={{ whiteSpace: "nowrap" }}>
                {item.url && (
                  <Tooltip title="Open on YouTube">
                    <IconButton
                      size="small"
                      component="a"
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <OpenInNewIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip
                  title={`Copy ${item.type === "stream" ? "stream" : "content"} ID ${item._id}`}
                >
                  <IconButton size="small" onClick={() => copyIds([item])}>
                    <CopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Dialog
      open={Boolean(target)}
      onClose={() => !assignMutation.isPending && onClose()}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle>Content in {target?.name}</DialogTitle>
      <DialogContent dividers>
        <TextField
          size="small"
          fullWidth
          placeholder="Search titles and descriptions across all of the channel's videos and streams"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: contentQuery.isFetching ? (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            ) : null,
          }}
          helperText={
            search
              ? `Showing matches from all channel content, not only ${target?.name ?? "this program"}.`
              : undefined
          }
        />
        {feedback && (
          <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ mb: 2 }}>
            {feedback.message}
          </Alert>
        )}
        {contentQuery.isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : contentQuery.isError ? (
          <Alert severity="error">
            {apiErrorMessage(contentQuery.error, "Could not load program content.")}
          </Alert>
        ) : sections.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {search ? `No videos or streams match "${search}".` : "No content assigned to this program."}
          </Typography>
        ) : (
          <Stack spacing={3}>
            {data && (data.contentTotal > data.content.length || data.streamTotal > data.streams.length) && (
              <Typography variant="caption" color="text.secondary">
                Showing the latest {data.content.length} of {data.contentTotal} episodes and{" "}
                {data.streams.length} of {data.streamTotal} streams. A video is grouped only when both
                sides are in this list.
              </Typography>
            )}
            {sections.map((section) => (
              <Box key={section.label}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {section.label} ({section.total})
                </Typography>
                {renderRows(section.items, section.showKind)}
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, gap: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: "auto" }}>
          {selected.size > 0 ? `${selected.size} selected` : "Select items to move them"}
        </Typography>
        {selected.size > 0 && (
          <Button
            size="small"
            startIcon={<CopyIcon fontSize="small" />}
            onClick={() => copyIds(selectedItems)}
          >
            Copy IDs
          </Button>
        )}
        <FormControl size="small" sx={{ minWidth: 280 }} disabled={selected.size === 0}>
          <InputLabel>Move to</InputLabel>
          <Select value={moveTo} label="Move to" onChange={(e) => setMoveTo(String(e.target.value))}>
            {legacyKind && (
              <ListSubheader>
                {legacyKind === "content" ? "Podcast programs" : "Livestream programs"}
              </ListSubheader>
            )}
            {legacyMoveOptions.map((prog) => (
              <MenuItem key={prog._id} value={`${LEGACY_MOVE_PREFIX}${prog._id}`}>
                {prog.name}
              </MenuItem>
            ))}
            {legacyKind && <ListSubheader>Channel programs</ListSubheader>}
            {programs.map((prog) => (
              <MenuItem key={prog._id} value={prog._id}>
                {prog.name}
              </MenuItem>
            ))}
            <MenuItem value={NO_CHANNEL_PROGRAM}>
              <em>No channel program</em>
            </MenuItem>
          </Select>
          {legacyMoveId && legacyMovableCount < selected.size && (
            <FormHelperText>
              Only {legacyMovableCount} selected {legacyItemType === "stream" ? "stream" : "episode"}
              (s) will move
            </FormHelperText>
          )}
        </FormControl>
        <Button
          variant="contained"
          disabled={
            selected.size === 0 ||
            !moveTo ||
            (legacyMoveId !== null && legacyMovableCount === 0) ||
            assignMutation.isPending
          }
          onClick={() => assignMutation.mutate()}
        >
          {assignMutation.isPending ? "Moving…" : "Move"}
        </Button>
        <Button onClick={onClose} disabled={assignMutation.isPending}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const appendCsv = (current: string, values: string[]): string =>
  joinCsv([...splitCsv(current), ...values]);

type ChannelProgramsSectionProps = {
  channelId: string;
  channelType?: string;
};

const ChannelProgramsSection: React.FC<ChannelProgramsSectionProps> = ({
  channelId,
  channelType,
}) => {
  const queryClient = useQueryClient();
  const isTvChannel = channelType === "tv";

  const [kindFilter, setKindFilter] = useState<KindFilter>("all");
  const [onlyUnlinked, setOnlyUnlinked] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<ProgramFormState>(emptyForm);
  const [createSlugManual, setCreateSlugManual] = useState(false);

  const [editTarget, setEditTarget] = useState<MediaChannelProgramRow | null>(null);
  const [editForm, setEditForm] = useState<ProgramFormState>(emptyForm);
  const [editSlugManual, setEditSlugManual] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<MediaChannelProgramRow | null>(null);

  const [mergeSource, setMergeSource] = useState<MediaChannelProgramRow | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState("");

  const [contentTarget, setContentTarget] = useState<ProgramContentTarget | null>(null);

  const [linkSource, setLinkSource] = useState<LegacyProgram | null>(null);
  const [linkTargetId, setLinkTargetId] = useState("");

  const [selectedLegacy, setSelectedLegacy] = useState<Set<string>>(() => new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  /** Create (`prog: null`) or edit a podcast / livestream program. */
  const [legacyDialog, setLegacyDialog] = useState<{
    kind: MediaChannelProgramKind;
    prog: LegacyProgram | null;
  } | null>(null);
  const [legacyEditForm, setLegacyEditForm] = useState({
    name: "",
    slug: "",
    slugManual: false,
    isActive: true,
    hidden: false,
  });
  const [legacyMergeSource, setLegacyMergeSource] = useState<LegacyProgram | null>(null);
  const [legacyMergeTargetId, setLegacyMergeTargetId] = useState("");
  /** `null` = by name. */
  const [itemCountOrder, setItemCountOrder] = useState<"asc" | "desc" | null>(null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const programsQuery = useQuery({
    queryKey: ["admin-media-channel-programs", channelId],
    queryFn: () => apiService.getAdminMediaChannelPrograms(channelId),
    enabled: Boolean(channelId),
  });

  const programs = useMemo(
    () => sortByName(programsQuery.data?.programs ?? []),
    [programsQuery.data?.programs],
  );
  const availableKinds = programsQuery.data?.availableKinds ?? [];

  const legacyPrograms = useMemo<Record<MediaChannelProgramKind, LegacyProgram[]>>(
    () => ({
      content: sortByName(
        (programsQuery.data?.podcastPrograms ?? []).map((p) => ({
          kind: "content" as const,
          _id: p._id,
          name: p.name,
          slug: p.slug || null,
          isActive: p.isActive,
          hidden: p.hidden,
          itemCount: p.contentCount,
          foreignStreamChannels: [],
          channelProgram: p.channelProgram,
        })),
      ),
      livestream: sortByName(
        (programsQuery.data?.livestreamPrograms ?? []).map((p) => ({
          kind: "livestream" as const,
          _id: p._id,
          name: p.name,
          slug: null,
          isActive: p.isActive,
          hidden: p.hidden,
          itemCount: p.streamCount,
          foreignStreamChannels: p.foreignStreamChannels ?? [],
          channelProgram: p.channelProgram,
        })),
      ),
    }),
    [programsQuery.data?.podcastPrograms, programsQuery.data?.livestreamPrograms],
  );

  const kindCounts = useMemo(
    () => ({
      all: programs.length,
      content: programs.filter((p) => p.kinds.includes("content")).length,
      livestream: programs.filter((p) => p.kinds.includes("livestream")).length,
    }),
    [programs],
  );

  const visiblePrograms = useMemo(
    () =>
      kindFilter === "all" ? programs : programs.filter((p) => p.kinds.includes(kindFilter)),
    [programs, kindFilter],
  );

  const allLegacyPrograms = useMemo(
    () => [...legacyPrograms.content, ...legacyPrograms.livestream],
    [legacyPrograms],
  );
  const unlinkedCount = allLegacyPrograms.filter((p) => !p.channelProgram).length;
  const visibleLegacyPrograms = useMemo(() => {
    const filtered = allLegacyPrograms.filter(
      (p) =>
        (kindFilter === "all" || p.kind === kindFilter) && (!onlyUnlinked || !p.channelProgram),
    );
    if (!itemCountOrder) return filtered;
    const sign = itemCountOrder === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => sign * (a.itemCount - b.itemCount));
  }, [allLegacyPrograms, kindFilter, onlyUnlinked, itemCountOrder]);

  const selectedLegacyPrograms = useMemo(
    () => allLegacyPrograms.filter((p) => selectedLegacy.has(legacyKey(p))),
    [allLegacyPrograms, selectedLegacy],
  );
  const legacyGroups = ALL_KINDS.map((kind) => ({
    kind,
    total: legacyPrograms[kind].length,
    unlinked: legacyPrograms[kind].filter((p) => !p.channelProgram).length,
    rows: visibleLegacyPrograms.filter((p) => p.kind === kind),
  })).filter(
    (group) =>
      (group.total > 0 || availableKinds.includes(group.kind)) &&
      (kindFilter === "all" || kindFilter === group.kind),
  );
  const emptyVisibleKeys = visibleLegacyPrograms
    .filter((p) => p.itemCount === 0 && !p.channelProgram)
    .map(legacyKey);

  const toggleLegacy = (keys: string[], checked: boolean) =>
    setSelectedLegacy((current) => {
      const next = new Set(current);
      for (const key of keys) {
        if (checked) next.add(key);
        else next.delete(key);
      }
      return next;
    });

  const invalidateProgramQueries = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin-media-channel-programs", channelId] });
    void queryClient.invalidateQueries({ queryKey: ["admin-media-channel-detail", channelId] });
    void queryClient.invalidateQueries({ queryKey: ["admin-media-channels"] });
  };

  const showSuccess = (message: string) => setSnackbar({ open: true, message, severity: "success" });
  const showError = (error: unknown, fallback: string) =>
    setSnackbar({ open: true, message: apiErrorMessage(error, fallback), severity: "error" });

  const formPayload = (form: ProgramFormState) => ({
    name: form.name.trim(),
    slug: form.slug.trim() || undefined,
    aliases: splitCsv(form.aliases),
    keywords: splitCsv(form.keywords),
    cues: splitCsv(form.cues),
    hashtags: splitCsv(form.hashtags),
    youtubePlaylistIds: splitCsv(form.youtubePlaylistIds),
    isActive: form.isActive,
    hidden: form.hidden,
    isDigital: isTvChannel ? form.isDigital : undefined,
    podcastProgramId: form.linkIds.content || null,
    livestreamProgramId: form.linkIds.livestream || null,
  });

  const createMutation = useMutation({
    mutationFn: () => apiService.createAdminMediaChannelProgram(channelId, formPayload(createForm)),
    onSuccess: (result) => {
      invalidateProgramQueries();
      setCreateOpen(false);
      showSuccess(`Channel program "${result.program.name}" created`);
    },
    onError: (error) => showError(error, "Could not create channel program"),
  });

  const editMutation = useMutation({
    mutationFn: () =>
      apiService.patchAdminMediaChannelProgram(channelId, editTarget!._id, formPayload(editForm)),
    onSuccess: () => {
      invalidateProgramQueries();
      setEditTarget(null);
      showSuccess("Channel program updated");
    },
    onError: (error) => showError(error, "Could not update channel program"),
  });

  const deleteMutation = useMutation({
    mutationFn: (programId: string) =>
      apiService.deleteAdminMediaChannelProgram(channelId, programId),
    onSuccess: () => {
      invalidateProgramQueries();
      setDeleteTarget(null);
      showSuccess("Channel program deleted");
    },
    onError: (error) => showError(error, "Could not delete channel program"),
  });

  const mergeMutation = useMutation({
    mutationFn: ({ source, targetId }: { source: MediaChannelProgramRow; targetId: string }) =>
      apiService.mergeAdminMediaChannelProgram(channelId, targetId, source._id),
    onSuccess: (result, { source }) => {
      invalidateProgramQueries();
      setMergeSource(null);
      showSuccess(`"${source.name}" merged into "${result.program.name}"`);
    },
    onError: (error) => showError(error, "Could not merge channel programs"),
  });

  const linkMutation = useMutation({
    mutationFn: ({ source, targetId }: { source: LegacyProgram; targetId: string }) =>
      apiService.patchAdminMediaChannelProgram(channelId, targetId, {
        [linkPayloadKey(source.kind)]: source._id,
      }),
    onSuccess: (result, { source }) => {
      invalidateProgramQueries();
      setLinkSource(null);
      showSuccess(`"${source.name}" linked to "${result.program.name}"`);
    },
    onError: (error) => showError(error, "Could not link program"),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (items: LegacyProgram[]) =>
      apiService.deleteAdminMediaChannelLegacyPrograms(channelId, {
        podcastProgramIds: items.filter((p) => p.kind === "content").map((p) => p._id),
        livestreamProgramIds: items.filter((p) => p.kind === "livestream").map((p) => p._id),
      }),
    onSuccess: (result) => {
      invalidateProgramQueries();
      setBulkDeleteOpen(false);
      setSelectedLegacy(new Set());
      const skipped =
        result.skipped.length > 0
          ? `; ${result.skipped.length} skipped (linked or with content)`
          : "";
      showSuccess(`${result.deletedCount} program(s) deleted${skipped}`);
    },
    onError: (error) => showError(error, "Could not delete programs"),
  });

  const legacyEditMutation = useMutation({
    mutationFn: async ({ kind, prog }: { kind: MediaChannelProgramKind; prog: LegacyProgram | null }) => {
      const name = legacyEditForm.name.trim();
      const payload = {
        name,
        isActive: legacyEditForm.isActive,
        hidden: legacyEditForm.hidden,
      };
      if (kind === "livestream") {
        if (prog) {
          await apiService.updateLivestreamProgram(prog._id, payload);
          return;
        }
        const livestreamChannelId = programsQuery.data?.legacyLivestreamChannelId;
        if (!livestreamChannelId) throw new Error("This channel has no linked livestream channel");
        const created = await apiService.createLivestreamProgram({
          channelId: livestreamChannelId,
          name,
          isActive: payload.isActive,
        });
        if (payload.hidden) {
          await apiService.updateLivestreamProgram(String(created.program._id), { hidden: true });
        }
        return;
      }
      const podcastId = programsQuery.data?.legacyPodcastId;
      if (!podcastId) throw new Error("This channel has no linked podcast");
      const podcastPayload = { ...payload, slug: legacyEditForm.slug.trim() || slugify(name) };
      if (prog) {
        await apiService.patchAdminChannelProgram(podcastId, prog._id, podcastPayload);
      } else {
        await apiService.createAdminChannelProgram(podcastId, podcastPayload);
      }
    },
    onSuccess: (_result, { prog }) => {
      invalidateProgramQueries();
      setLegacyDialog(null);
      showSuccess(prog ? "Program updated" : `Program "${legacyEditForm.name.trim()}" created`);
    },
    onError: (error, { prog }) =>
      showError(error, prog ? "Could not update program" : "Could not create program"),
  });

  const openLegacyEditDialog = (prog: LegacyProgram) => {
    setLegacyEditForm({
      name: prog.name,
      slug: prog.slug ?? "",
      slugManual: Boolean(prog.slug) && prog.slug !== slugify(prog.name),
      isActive: prog.isActive,
      hidden: prog.hidden,
    });
    setLegacyDialog({ kind: prog.kind, prog });
  };

  const openLegacyCreateDialog = (kind: MediaChannelProgramKind) => {
    setLegacyEditForm({ name: "", slug: "", slugManual: false, isActive: true, hidden: false });
    setLegacyDialog({ kind, prog: null });
  };

  const legacyMergeMutation = useMutation({
    mutationFn: ({ source, targetId }: { source: LegacyProgram; targetId: string }) =>
      apiService.mergeAdminMediaChannelLegacyProgram(channelId, {
        kind: source.kind,
        sourceId: source._id,
        targetId,
      }),
    onSuccess: (result, { source }) => {
      invalidateProgramQueries();
      setLegacyMergeSource(null);
      setSelectedLegacy((current) => {
        const next = new Set(current);
        next.delete(legacyKey(source));
        return next;
      });
      showSuccess(
        `"${source.name}" merged into "${result.target.name}" (${result.movedCount} ${
          source.kind === "content" ? "episode(s)" : "stream(s)"
        } moved)`,
      );
    },
    onError: (error) => showError(error, "Could not merge programs"),
  });

  const legacyMergeTargets = legacyMergeSource
    ? legacyPrograms[legacyMergeSource.kind].filter((p) => p._id !== legacyMergeSource._id)
    : [];
  const legacyMergeTarget = legacyMergeTargets.find((p) => p._id === legacyMergeTargetId) ?? null;

  const bulkDeleteBlocked = selectedLegacyPrograms.filter(
    (p) => p.channelProgram || p.itemCount > 0,
  ).length;

  const mergeTargets = useMemo(
    () =>
      mergeSource
        ? programs.filter((p) => p._id !== mergeSource._id)
        : [],
    [programs, mergeSource],
  );

  const linkTargets = useMemo(
    () => (linkSource ? programs.filter((p) => !p.kinds.includes(linkSource.kind)) : []),
    [programs, linkSource],
  );

  const openCreateDialog = (from?: LegacyProgram) => {
    const form = emptyForm();
    if (from) {
      form.name = from.name;
      form.slug = slugify(from.name);
      form.linkIds[from.kind] = from._id;
      form.isActive = from.isActive;
      form.hidden = from.hidden;
    }
    setCreateForm(form);
    setCreateSlugManual(false);
    setCreateOpen(true);
  };

  const openEditDialog = (prog: MediaChannelProgramRow) => {
    setEditForm(formFromProgram(prog));
    setEditSlugManual(Boolean(prog.slug));
    setEditTarget(prog);
  };

  const renderFormFields = (
    form: ProgramFormState,
    setForm: React.Dispatch<React.SetStateAction<ProgramFormState>>,
    opts: {
      slugManual: boolean;
      setSlugManual: (manual: boolean) => void;
      currentRowId?: string;
    },
  ) => (
    <Stack spacing={2} sx={{ pt: 0.5 }}>
      {ALL_KINDS.map((kind) => {
        const options = legacyPrograms[kind];
        const disabled = !availableKinds.includes(kind) && !form.linkIds[kind];
        return (
          <FormControl key={kind} fullWidth size="small" disabled={disabled}>
            <InputLabel>{LEGACY_PROGRAM_LABELS[kind]}</InputLabel>
            <Select
              value={form.linkIds[kind]}
              label={LEGACY_PROGRAM_LABELS[kind]}
              onChange={(e) => {
                const linkId = String(e.target.value);
                const linked = options.find((p) => p._id === linkId);
                setForm((f) => {
                  const next = { ...f, linkIds: { ...f.linkIds, [kind]: linkId } };
                  if (!linked || f.name.trim()) return next;
                  return {
                    ...next,
                    name: linked.name,
                    slug: opts.slugManual ? f.slug : slugify(linked.name),
                  };
                });
              }}
            >
              <MenuItem value="">
                <em>Not linked</em>
              </MenuItem>
              {options.map((p) => (
                <MenuItem key={p._id} value={p._id}>
                  {p.name}
                  {p.channelProgram && p.channelProgram._id !== opts.currentRowId
                    ? ` · moves from "${p.channelProgram.name}"`
                    : ""}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {disabled
                ? kind === "content"
                  ? "This channel has no linked podcast"
                  : "This channel has no linked livestream channel"
                : `The ${kind === "content" ? "podcast" : "livestream"} program and its ${
                    kind === "content" ? "episodes" : "streams"
                  } are kept; they just get linked to this channel program.`}
            </FormHelperText>
          </FormControl>
        );
      })}
      <TextField
        label="Name"
        value={form.name}
        onChange={(e) => {
          const name = e.target.value;
          setForm((f) => ({ ...f, name, slug: opts.slugManual ? f.slug : slugify(name) }));
        }}
        fullWidth
        required
        size="small"
      />
      <TextField
        label="Slug"
        value={form.slug}
        onChange={(e) => {
          opts.setSlugManual(true);
          setForm((f) => ({ ...f, slug: e.target.value }));
        }}
        fullWidth
        size="small"
        helperText="Auto-generated from the name; you can edit it."
      />
      <TextField
        label="Aliases (comma-separated)"
        value={form.aliases}
        onChange={(e) => setForm((f) => ({ ...f, aliases: e.target.value }))}
        fullWidth
        size="small"
        helperText="Alternate names used in program classification"
      />
      <ProgramFieldSuggestions
        channelId={channelId}
        linkIds={form.linkIds}
        field="aliases"
        currentValues={[form.name, ...splitCsv(form.aliases)]}
        onAdd={(values) => setForm((f) => ({ ...f, aliases: appendCsv(f.aliases, values) }))}
      />
      <TextField
        label="Keywords (comma-separated)"
        value={form.keywords}
        onChange={(e) => setForm((f) => ({ ...f, keywords: e.target.value }))}
        fullWidth
        size="small"
        helperText="Matched in episode titles"
      />
      <TextField
        label="Cues (comma-separated)"
        value={form.cues}
        onChange={(e) => setForm((f) => ({ ...f, cues: e.target.value }))}
        fullWidth
        size="small"
        helperText="Matched in episode description (e.g. clip extraido)"
      />
      <TextField
        label="Hashtags without # (comma-separated)"
        value={form.hashtags}
        onChange={(e) => setForm((f) => ({ ...f, hashtags: e.target.value }))}
        fullWidth
        size="small"
      />
      <ProgramFieldSuggestions
        channelId={channelId}
        linkIds={form.linkIds}
        field="hashtags"
        currentValues={splitCsv(form.hashtags)}
        onAdd={(values) => setForm((f) => ({ ...f, hashtags: appendCsv(f.hashtags, values) }))}
      />
      <TextField
        label="YouTube playlist IDs (comma-separated)"
        value={form.youtubePlaylistIds}
        onChange={(e) => setForm((f) => ({ ...f, youtubePlaylistIds: e.target.value }))}
        fullWidth
        size="small"
      />
      <FormControlLabel
        control={
          <Switch
            checked={form.isActive}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
          />
        }
        label="Active"
      />
      <FormControlLabel
        control={
          <Switch
            checked={form.hidden}
            onChange={(e) => setForm((f) => ({ ...f, hidden: e.target.checked }))}
          />
        }
        label="Hidden (excluded from rankings and explorer)"
      />
      {isTvChannel && (
        <FormControlLabel
          control={
            <Switch
              checked={form.isDigital}
              onChange={(e) => setForm((f) => ({ ...f, isDigital: e.target.checked }))}
            />
          }
          label="100% digital"
        />
      )}
    </Stack>
  );

  const busy =
    createMutation.isPending ||
    editMutation.isPending ||
    deleteMutation.isPending ||
    mergeMutation.isPending ||
    linkMutation.isPending ||
    bulkDeleteMutation.isPending ||
    legacyEditMutation.isPending ||
    legacyMergeMutation.isPending;

  const renderLinkedCell = (prog: MediaChannelProgramRow, kind: MediaChannelProgramKind) => {
    if (!linkedLegacyId(prog, kind)) {
      return (
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
      );
    }
    return (
      <Chip
        size="small"
        variant="outlined"
        color={kind === "livestream" ? "error" : "primary"}
        label={linkedLegacyName(prog, kind) ?? "(missing)"}
      />
    );
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        gap={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Channel programs
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Group this channel&apos;s podcast and livestream programs. Linking never removes them
            or changes their content.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={kindFilter}
            onChange={(_e, value: KindFilter | null) => value && setKindFilter(value)}
          >
            <ToggleButton value="all">All ({kindCounts.all})</ToggleButton>
            <ToggleButton value="content">
              {KIND_LABELS.content} ({kindCounts.content})
            </ToggleButton>
            <ToggleButton value="livestream">
              {KIND_LABELS.livestream} ({kindCounts.livestream})
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => openCreateDialog()}
            disabled={busy}
          >
            Add channel program
          </Button>
        </Stack>
      </Stack>

      {programsQuery.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : programsQuery.isError ? (
        <Alert severity="error">
          {apiErrorMessage(programsQuery.error, "Could not load programs.")}
        </Alert>
      ) : (
        <>
          {visiblePrograms.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {programs.length === 0
                ? "No channel programs yet. Create one from a podcast or livestream program below."
                : "No channel programs of this kind."}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Channel program</TableCell>
                    <TableCell>Podcast program</TableCell>
                    <TableCell>Livestream program</TableCell>
                    <TableCell>Aliases / keywords / cues</TableCell>
                    <TableCell align="right">Episodes</TableCell>
                    <TableCell align="right" width={168} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visiblePrograms.map((prog) => (
                    <TableRow key={prog._id} hover>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2">{prog.name}</Typography>
                          {prog.slug ? (
                            <Typography variant="caption" color="text.secondary">
                              {prog.slug}
                            </Typography>
                          ) : null}
                          <Stack direction="row" gap={0.5} flexWrap="wrap">
                            {!prog.isActive && <Chip size="small" label="inactive" />}
                            {prog.hidden && (
                              <Chip size="small" label="hidden" variant="outlined" />
                            )}
                            {prog.isDigital && (
                              <Chip size="small" label="digital" color="info" variant="outlined" />
                            )}
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell>{renderLinkedCell(prog, "content")}</TableCell>
                      <TableCell>{renderLinkedCell(prog, "livestream")}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" display="block">
                          A: {previewList(prog.aliases)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          K: {previewList(prog.keywords)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          C: {previewList(prog.cues)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {prog.kinds.includes("content") ? prog.contentCount : "—"}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="See assigned content">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setContentTarget({
                                name: prog.name,
                                query: { channelProgramId: prog._id },
                              })
                            }
                          >
                            <ContentIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit channel program">
                          <span>
                            <IconButton
                              size="small"
                              disabled={busy}
                              onClick={() => openEditDialog(prog)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            prog.kinds.length === 0
                              ? "Nothing linked to merge"
                              : "Merge into another channel program"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={busy || prog.kinds.length === 0}
                              onClick={() => {
                                setMergeTargetId("");
                                setMergeSource(prog);
                              }}
                            >
                              <MergeIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete channel program">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={busy}
                              onClick={() => setDeleteTarget(prog)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Divider sx={{ my: 2.5 }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            gap={1}
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Podcast &amp; livestream programs ({unlinkedCount} not linked)
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={onlyUnlinked}
                  onChange={(e) => setOnlyUnlinked(e.target.checked)}
                />
              }
              label={<Typography variant="body2">Only not linked</Typography>}
            />
          </Stack>
          {visibleLegacyPrograms.length > 0 && (
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
              <Button
                size="small"
                disabled={busy || emptyVisibleKeys.length === 0}
                onClick={() => setSelectedLegacy(new Set(emptyVisibleKeys))}
              >
                Select empty ({emptyVisibleKeys.length})
              </Button>
              {selectedLegacy.size > 0 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {selectedLegacyPrograms.length} selected
                  </Typography>
                  <Button size="small" disabled={busy} onClick={() => setSelectedLegacy(new Set())}>
                    Clear
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteIcon fontSize="small" />}
                    disabled={busy || selectedLegacyPrograms.length === 0}
                    onClick={() => setBulkDeleteOpen(true)}
                  >
                    Delete selected
                  </Button>
                </>
              )}
            </Stack>
          )}
          {legacyGroups.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {allLegacyPrograms.length === 0
                ? "This channel has no podcast or livestream programs."
                : "This channel has no programs of this kind."}
            </Typography>
          ) : (
            <Stack spacing={2.5}>
              {legacyGroups.map((group) => {
                const groupKeys = group.rows.map(legacyKey);
                const groupSelected = groupKeys.filter((key) => selectedLegacy.has(key)).length;
                return (
                  <Box key={group.kind}>
                    <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={group.kind === "livestream" ? "error" : "primary"}
                        label={KIND_LABELS[group.kind]}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {group.kind === "content" ? "Podcast programs" : "Livestream programs"} (
                        {group.total}, {group.unlinked} not linked)
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon fontSize="small" />}
                        sx={{ ml: "auto" }}
                        disabled={busy || !availableKinds.includes(group.kind)}
                        onClick={() => openLegacyCreateDialog(group.kind)}
                      >
                        Add {group.kind === "content" ? "podcast" : "livestream"} program
                      </Button>
                    </Stack>
                    {group.rows.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                        {group.total === 0
                          ? `No ${group.kind === "content" ? "podcast" : "livestream"} programs yet.`
                          : `Every ${
                              group.kind === "content" ? "podcast" : "livestream"
                            } program is linked to a channel program.`}
                      </Typography>
                    ) : (
                      <TableContainer>
                        <Table size="small" sx={{ tableLayout: "fixed" }}>
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox" width={48}>
                                <Checkbox
                                  size="small"
                                  checked={groupSelected > 0 && groupSelected === groupKeys.length}
                                  indeterminate={
                                    groupSelected > 0 && groupSelected < groupKeys.length
                                  }
                                  onChange={(e) => toggleLegacy(groupKeys, e.target.checked)}
                                />
                              </TableCell>
                              <TableCell>Program</TableCell>
                              <TableCell width={220}>Channel program</TableCell>
                              <TableCell
                                align="right"
                                width={120}
                                sortDirection={itemCountOrder ?? false}
                              >
                                <TableSortLabel
                                  active={itemCountOrder !== null}
                                  direction={itemCountOrder ?? "desc"}
                                  onClick={() =>
                                    setItemCountOrder((order) =>
                                      order === null ? "desc" : order === "desc" ? "asc" : null,
                                    )
                                  }
                                >
                                  {group.kind === "content" ? "Episodes" : "Streams"}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right" width={204} />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {group.rows.map((prog) => (
                              <TableRow
                                key={legacyKey(prog)}
                                hover
                                selected={selectedLegacy.has(legacyKey(prog))}
                              >
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    size="small"
                                    checked={selectedLegacy.has(legacyKey(prog))}
                                    onChange={(e) =>
                                      toggleLegacy([legacyKey(prog)], e.target.checked)
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <Stack
                                    direction="row"
                                    gap={0.5}
                                    alignItems="center"
                                    flexWrap="wrap"
                                  >
                                    <Typography variant="body2">{prog.name}</Typography>
                                    {!prog.isActive && <Chip size="small" label="inactive" />}
                                    {prog.hidden && (
                                      <Chip size="small" label="hidden" variant="outlined" />
                                    )}
                                    {prog.foreignStreamChannels.length > 0 && (
                                      <Tooltip
                                        title={`Streams from other livestream channels: ${prog.foreignStreamChannels
                                          .map((c) => `${c.channelName} (${c.count})`)
                                          .join(", ")}`}
                                      >
                                        <Chip
                                          size="small"
                                          color="warning"
                                          label={`${prog.foreignStreamChannels.reduce(
                                            (sum, c) => sum + c.count,
                                            0,
                                          )} from other channel`}
                                        />
                                      </Tooltip>
                                    )}
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  {prog.channelProgram ? (
                                    <Typography variant="body2">
                                      {prog.channelProgram.name}
                                    </Typography>
                                  ) : (
                                    <Typography variant="body2" color="text.disabled">
                                      Not linked
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {prog.itemCount.toLocaleString("es-CL")}
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title="See assigned content">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        setContentTarget({
                                          name: prog.name,
                                          query:
                                            prog.kind === "content"
                                              ? { podcastProgramId: prog._id }
                                              : {
                                                  livestreamProgramId: prog._id,
                                                },
                                        })
                                      }
                                    >
                                      <ContentIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip
                                    title={`Edit ${
                                      prog.kind === "content" ? "podcast" : "livestream"
                                    } program`}
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={busy}
                                        onClick={() => openLegacyEditDialog(prog)}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Create channel program from this">
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={busy}
                                        onClick={() => openCreateDialog(prog)}
                                      >
                                        <AddIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Link to an existing channel program">
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={busy || programs.length === 0}
                                        onClick={() => {
                                          setLinkTargetId("");
                                          setLinkSource(prog);
                                        }}
                                      >
                                        <LinkIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip
                                    title={`Merge into another ${
                                      prog.kind === "content" ? "podcast" : "livestream"
                                    } program`}
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={busy || legacyPrograms[prog.kind].length < 2}
                                        onClick={() => {
                                          setLegacyMergeTargetId("");
                                          setLegacyMergeSource(prog);
                                        }}
                                      >
                                        <MergeIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </>
      )}

      <Dialog
        open={createOpen}
        onClose={() => !createMutation.isPending && setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add channel program</DialogTitle>
        <DialogContent dividers>
          {renderFormFields(createForm, setCreateForm, {
            slugManual: createSlugManual,
            setSlugManual: setCreateSlugManual,
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending || !createForm.name.trim()}
          >
            {createMutation.isPending ? "Creating…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editTarget)}
        onClose={() => !editMutation.isPending && setEditTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit channel program</DialogTitle>
        <DialogContent dividers>
          {renderFormFields(editForm, setEditForm, {
            slugManual: editSlugManual,
            setSlugManual: setEditSlugManual,
            ...(editTarget ? { currentRowId: editTarget._id } : {}),
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)} disabled={editMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => editMutation.mutate()}
            disabled={editMutation.isPending || !editForm.name.trim()}
          >
            {editMutation.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleteMutation.isPending && setDeleteTarget(null)}
      >
        <DialogTitle>Delete channel program</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteTarget?.name}</strong>? Its linked podcast and livestream
            programs, and their episodes and streams, are kept; they just stop pointing to this
            channel program.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget._id)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(mergeSource)}
        onClose={() => !mergeMutation.isPending && setMergeSource(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Merge channel program</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <DialogContentText>
              Move the {mergeSource?.kinds.map((kind) => LEGACY_PROGRAM_LABELS[kind].toLowerCase()).join(" and ")}{" "}
              of <strong>{mergeSource?.name}</strong> into another channel program, then delete{" "}
              <strong>{mergeSource?.name}</strong>. If the target already has a podcast or
              livestream program, those programs and their episodes / streams are merged into
              it. Aliases, keywords, cues and hashtags are combined.
            </DialogContentText>
            {mergeTargets.length === 0 ? (
              <Alert severity="info">
                There is no other channel program to merge into.
              </Alert>
            ) : (
              <FormControl fullWidth size="small">
                <InputLabel>Merge into</InputLabel>
                <Select
                  value={mergeTargetId}
                  label="Merge into"
                  onChange={(e) => setMergeTargetId(String(e.target.value))}
                >
                  {mergeTargets.map((prog) => (
                    <MenuItem key={prog._id} value={prog._id}>
                      {prog.name}
                      {prog.kinds.length > 0
                        ? ` (${prog.kinds.map((kind) => KIND_LABELS[kind]).join(" + ")})`
                        : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeSource(null)} disabled={mergeMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={mergeMutation.isPending || !mergeTargetId}
            onClick={() =>
              mergeSource && mergeMutation.mutate({ source: mergeSource, targetId: mergeTargetId })
            }
          >
            {mergeMutation.isPending ? "Merging…" : "Merge"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(linkSource)}
        onClose={() => !linkMutation.isPending && setLinkSource(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Link to channel program</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <DialogContentText>
              Link the {linkSource ? LEGACY_PROGRAM_LABELS[linkSource.kind].toLowerCase() : ""}{" "}
              <strong>{linkSource?.name}</strong> to a channel program. Its{" "}
              {linkSource?.kind === "content" ? "episodes" : "streams"} are tagged with the channel
              program; nothing else changes.
              {linkSource?.channelProgram
                ? ` It is currently linked to "${linkSource.channelProgram.name}" and will move.`
                : ""}
            </DialogContentText>
            {linkTargets.length === 0 ? (
              <Alert severity="info">
                Every channel program already has a{" "}
                {linkSource?.kind === "content" ? "podcast" : "livestream"} program. Create a new
                channel program instead.
              </Alert>
            ) : (
              <FormControl fullWidth size="small">
                <InputLabel>Channel program</InputLabel>
                <Select
                  value={linkTargetId}
                  label="Channel program"
                  onChange={(e) => setLinkTargetId(String(e.target.value))}
                >
                  {linkTargets.map((prog) => (
                    <MenuItem key={prog._id} value={prog._id}>
                      {prog.name}
                      {prog.kinds.length > 0
                        ? ` (${prog.kinds.map((kind) => KIND_LABELS[kind]).join(" + ")})`
                        : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkSource(null)} disabled={linkMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={linkMutation.isPending || !linkTargetId}
            onClick={() =>
              linkSource && linkMutation.mutate({ source: linkSource, targetId: linkTargetId })
            }
          >
            {linkMutation.isPending ? "Linking…" : "Link"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(legacyDialog)}
        onClose={() => !legacyEditMutation.isPending && setLegacyDialog(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {legacyDialog?.prog ? "Edit" : "Add"}{" "}
          {legacyDialog?.kind === "content" ? "podcast" : "livestream"} program
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              label="Name"
              value={legacyEditForm.name}
              onChange={(e) => {
                const name = e.target.value;
                setLegacyEditForm((f) => ({
                  ...f,
                  name,
                  slug: f.slugManual ? f.slug : slugify(name),
                }));
              }}
              fullWidth
              required
              size="small"
              helperText={
                legacyDialog?.prog?.channelProgram
                  ? `Linked to channel program "${legacyDialog.prog.channelProgram.name}", which keeps its own name.`
                  : undefined
              }
            />
            {legacyDialog?.kind === "content" && (
              <TextField
                label="Slug"
                value={legacyEditForm.slug}
                onChange={(e) =>
                  setLegacyEditForm((f) => ({ ...f, slug: e.target.value, slugManual: true }))
                }
                fullWidth
                size="small"
                helperText="Auto-generated from the name; you can edit it. Changing it changes the program page URL."
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={legacyEditForm.isActive}
                  onChange={(e) =>
                    setLegacyEditForm((f) => ({ ...f, isActive: e.target.checked }))
                  }
                />
              }
              label="Active"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={legacyEditForm.hidden}
                  onChange={(e) => setLegacyEditForm((f) => ({ ...f, hidden: e.target.checked }))}
                />
              }
              label="Hidden (excluded from rankings and explorer)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setLegacyDialog(null)}
            disabled={legacyEditMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={legacyEditMutation.isPending || !legacyEditForm.name.trim()}
            onClick={() => legacyDialog && legacyEditMutation.mutate(legacyDialog)}
          >
            {legacyEditMutation.isPending
              ? "Saving…"
              : legacyDialog?.prog
                ? "Save"
                : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(legacyMergeSource)}
        onClose={() => !legacyMergeMutation.isPending && setLegacyMergeSource(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Merge {legacyMergeSource?.kind === "content" ? "podcast" : "livestream"} program
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <DialogContentText>
              Move all {legacyMergeSource?.itemCount.toLocaleString("es-CL")}{" "}
              {legacyMergeSource?.kind === "content" ? "episodes" : "streams"} of{" "}
              <strong>{legacyMergeSource?.name}</strong> into another program, then delete{" "}
              <strong>{legacyMergeSource?.name}</strong>. Its keywords and hashtags are added to the
              target. Moved items take the target&apos;s channel program.
            </DialogContentText>
            {legacyMergeSource?.channelProgram && (
              <Alert severity="info">
                {legacyMergeSource.name} is linked to &quot;{legacyMergeSource.channelProgram.name}
                &quot;; that link is removed.
              </Alert>
            )}
            <FormControl fullWidth size="small">
              <InputLabel>Merge into</InputLabel>
              <Select
                value={legacyMergeTargetId}
                label="Merge into"
                onChange={(e) => setLegacyMergeTargetId(String(e.target.value))}
              >
                {legacyMergeTargets.map((p) => (
                  <MenuItem key={p._id} value={p._id}>
                    {p.name} ({p.itemCount.toLocaleString("es-CL")})
                    {p.channelProgram ? ` · in "${p.channelProgram.name}"` : ""}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {legacyMergeTarget
                  ? legacyMergeTarget.channelProgram
                    ? `Moved items join channel program "${legacyMergeTarget.channelProgram.name}".`
                    : "The target is not linked to a channel program."
                  : " "}
              </FormHelperText>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setLegacyMergeSource(null)}
            disabled={legacyMergeMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={legacyMergeMutation.isPending || !legacyMergeTargetId}
            onClick={() =>
              legacyMergeSource &&
              legacyMergeMutation.mutate({
                source: legacyMergeSource,
                targetId: legacyMergeTargetId,
              })
            }
          >
            {legacyMergeMutation.isPending ? "Merging…" : "Merge"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkDeleteOpen}
        onClose={() => !bulkDeleteMutation.isPending && setBulkDeleteOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Delete {selectedLegacyPrograms.length} program(s)</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <DialogContentText>
              Permanently delete these podcast / livestream programs. Programs linked to a channel
              program or with any episode or stream assigned are skipped.
            </DialogContentText>
            {bulkDeleteBlocked > 0 && (
              <Alert severity="warning">
                {bulkDeleteBlocked} selected program(s) are linked or have content and will be
                skipped.
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              {previewList(
                selectedLegacyPrograms.map((p) => p.name),
                10,
              )}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleteMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={bulkDeleteMutation.isPending || selectedLegacyPrograms.length === 0}
            onClick={() => bulkDeleteMutation.mutate(selectedLegacyPrograms)}
          >
            {bulkDeleteMutation.isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <ProgramContentDialog
        key={contentTarget ? JSON.stringify(contentTarget.query) : "closed"}
        channelId={channelId}
        target={contentTarget}
        legacyPrograms={legacyPrograms}
        programs={programs}
        onClose={() => setContentTarget(null)}
        onAssigned={invalidateProgramQueries}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default ChannelProgramsSection;
